import { StyleRulesCallback, Theme, Typography, WithStyles, withStyles } from '@material-ui/core';
import * as React from 'react';
import { required, Validator } from '../../../shared/validation';
import FormTextField from '../../../components/FormTextField';
import { DataSelector, Field, getFormValues } from 'redux-form';
import { BpM } from '../../../shared/brakepoints';
import FormSelectField from '../../../components/FormSelectField';
import { PrivatePersonDisclosureData } from '../../../model/disclosureData';
import { connect } from 'react-redux';
import { RootState } from '../../../rootState';
import Translator from '../../../components/Translator';
import Translate from '../../../components/Translate';
import { defaultMemoize } from 'reselect';

type CssClasses = 'fieldContainer' | 'fullRow' | 'halfRow' | 'bigRow';
const styles: StyleRulesCallback<CssClasses> = (theme: Theme) => ({
  fieldContainer: {
    display: 'flex',
    flexFlow: 'column',
    '& > *': {
      flex: '0 0 100%',
    },
    [BpM()]: {
      display: 'flex',
      margin: '0px -5px 0px',
      width: '500px',
      flexFlow: 'row wrap',
      alignItems: 'flex-end',
      '& > *': {
        flex: '0 0 250px',
        paddingLeft: '5px',
        paddingRight: '5px',
        maxWidth: '250px',
        width: '250px',
      },
    },
  },
  fullRow: {
    flex: '0 0 100%',
    maxWidth: '100%',
  },
  halfRow: {
    flex: '0 0 100%',
    maxWidth: '100%',
    [BpM()]: {
      '& > *': {
        width: '250px',
        paddingRight: '10px',
      },
    },
  },
  bigRow: {
    flex: '0 0 100%',
    maxWidth: '100%',
    [BpM()]: {
      '& > *': {
        width: '320px',
        paddingRight: '10px',
      },
    },
  },
});

interface StateProps {
  partner1NotInGermany: boolean;
}

class PrivatePersonRegistrationDisclosureFormData extends React.Component<WithStyles<CssClasses> & StateProps> {
  private translatedRequire = defaultMemoize((translation: string) => {
    return required(translation);
  });

  private justRequired = defaultMemoize((requiredValidator: Validator) => {
    return [requiredValidator];
  });

  public render() {
    let { classes, partner1NotInGermany } = this.props;
    return (
      <Translator>
        {translate => {
          const req = this.translatedRequire(translate('missing'));
          const justRequired = this.justRequired(req);
          return (
            <>
              <Typography variant={'h6'}>
                <Translate entry="disclosureHeaderPersonalData"/>
              </Typography>

              <div className={classes.fieldContainer}>
                <div className={classes.halfRow}>
                  <Field
                    name="partner1Salutation"
                    requireMark
                    component={FormSelectField}
                    validate={justRequired}
                    label={translate('title')}
                    fullWidth={false}
                    options={[
                      { value: '', text: translate('pleaseSelect') },
                      { value: 'Herr', text: translate('mr') },
                      { value: 'Frau', text: translate('mrs') },
                    ]}
                  />
                </div>
                <Field
                  name="partner1FirstName"
                  requireMark
                  component={FormTextField}
                  validate={justRequired}
                  label={translate('firstName')}
                  fullWidth={true}
                />
                <Field
                  name="partner1LastName"
                  requireMark
                  component={FormTextField}
                  validate={justRequired}
                  label={translate('lastName')}
                  fullWidth={true}
                />
                <Field
                  name="partner1Street"
                  requireMark
                  component={FormTextField}
                  validate={justRequired}
                  label={translate('street')}
                  fullWidth={true}
                />
                <Field
                  name="partner1HouseNumber"
                  requireMark
                  component={FormTextField}
                  validate={justRequired}
                  label={translate('housenumber')}
                  fullWidth={true}
                />
                <Field
                  name="partner1ZipCode"
                  requireMark
                  component={FormTextField}
                  validate={justRequired}
                  label={translate('zipCode')}
                  fullWidth={true}
                />
                <Field
                  name="partner1City"
                  requireMark
                  component={FormTextField}
                  validate={justRequired}
                  label={translate('city')}
                  fullWidth={true}
                />
                <Field
                  name="partner1RegisterAddress"
                  requireMark
                  component={FormSelectField}
                  validate={justRequired}
                  label={translate('partner1RegisterAddress')}
                  fullWidth={true}
                  options={[
                    { value: '', text: translate('pleaseSelect') },
                    { value: '0', text: translate('germany') },
                    { value: '1', text: translate('notGermany') },
                  ]}
                />
                <Field
                  name="partner1NoUSCitizen"
                  requireMark
                  component={FormSelectField}
                  validate={justRequired}
                  label={translate('partner1NoUSCitizen')}
                  fullWidth={true}
                  toolTipHtml={translate('fatcaInfo')}
                  options={[
                    { value: '', text: translate('pleaseSelect') },
                    { value: '0', text: translate('yesUsCitizen') },
                    {
                      value: '1',
                      text: translate('noUsCitizen'),
                    },
                  ]}
                />
                <div className={classes.bigRow}>
                  <Field
                    name="partner1Nationality"
                    requireMark
                    component={FormSelectField}
                    validate={justRequired}
                    label={translate('partner1Nationality')}
                    fullWidth={false}
                    options={[
                      { value: '', text: translate('pleaseSelect') },
                      { value: 'DE', text: translate('euCountryGermany') },
                      { value: '', text: '---------------------' },
                      { value: 'BE', text: translate('euCountryBelgium') },
                      { value: 'BG', text: translate('euCountryBulgaria') },
                      { value: 'DK', text: translate('euCountryDenmark') },
                      { value: 'EE', text: translate('euCountryEstonia') },
                      { value: 'FI', text: translate('euCountryFinland') },
                      { value: 'FR', text: translate('euCountryFrance') },
                      { value: 'GR', text: translate('euCountryGreece') },
                      { value: 'NL', text: translate('euCountryNetherlands') },
                      { value: 'IT', text: translate('euCountryItaly') },
                      { value: 'IE', text: translate('euCountryIreland') },
                      { value: 'HR', text: translate('euCountryCroatia') },
                      { value: 'LV', text: translate('euCountryLatvia') },
                      { value: 'LT', text: translate('euCountryLithuania') },
                      { value: 'LU', text: translate('euCountryLuxembourg') },
                      { value: 'MT', text: translate('euCountryMalta') },
                      { value: 'AT', text: translate('euCountryAustria') },
                      { value: 'PL', text: translate('euCountryPoland') },
                      { value: 'PT', text: translate('euCountryPortugal') },
                      { value: 'RO', text: translate('euCountryRomania') },
                      { value: 'SE', text: translate('euCountrySweden') },
                      { value: 'SK', text: translate('euCountrySlovakia') },
                      { value: 'SI', text: translate('euCountrySlovenia') },
                      { value: 'ES', text: translate('euCountrySpain') },
                      { value: 'CZ', text: translate('euCountryCzech') },
                      { value: 'HU', text: translate('euCountryHungary') },
                      { value: 'CY', text: translate('euCountryCyprus') },
                    ]}
                  />
                </div>
                <div className={classes.bigRow}>
                  <Field
                    name="partner1MaritalStatus"
                    requireMark
                    component={FormSelectField}
                    validate={justRequired}
                    label={translate('partner1MaritalStatus')}
                    fullWidth={false}
                    options={[
                      { value: '', text: translate('pleaseSelect') },
                      { value: '0', text: translate('maritalSingle') },
                      { value: '1', text: translate('maritalMarried') },
                      { value: '2', text: translate('maritalParted') },
                      { value: '3', text: translate('maritalWidowed') },
                      { value: '4', text: translate('maritalDivorced') },
                      { value: '5', text: translate('maritalDivorcedNoAlimony') },
                      { value: '6', text: translate('maritalPartnership') },
                      { value: '7', text: translate('maritalRegisteredLifePartnership') },
                    ]}
                  />
                </div>

                <div className={classes.bigRow}>
                  <Field
                    name="partner1Phonenumber"
                    requireMark
                    component={FormTextField}
                    validate={justRequired}
                    label={translate('phonenumber')}
                    fullWidth={true}
                  />
                </div>
                <div className={classes.bigRow}>
                  <Field
                    name="partner1Mail"
                    requireMark
                    component={FormTextField}
                    validate={justRequired}
                    label={translate('mail')}
                    fullWidth={true}
                  />
                </div>
              </div>
            </>
          );
        }}
      </Translator>
    );
  }
}

const mapStateToProps = (state: RootState): StateProps => {
  const formValues = (getFormValues as DataSelector<PrivatePersonDisclosureData>)('privateperson')(state);

  return {
    partner1NotInGermany: formValues != null && formValues.partner1RegisterAddress === '1',
  };
};

export default connect(mapStateToProps)(withStyles(styles)(PrivatePersonRegistrationDisclosureFormData));



// WEBPACK FOOTER //
// ./src/routes/disclosure/components/PrivatePersonRegistrationDisclosureFormData.tsx